import { useState, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';

/**
 * DesktopSwitch - Reusable component to switch between desktop and mobile views
 * 
 * Props:
 * - desktop: Component to render on desktop (width >= 800 and not mobile device)
 * - mobile: Component to render on mobile (width < 800 or mobile device)
 * - desktopWidth: Minimum width for desktop view (default: 800)
 * - desktopProps: Props to spread onto the desktop component
 * - mobileProps: Props to spread onto the mobile component
 * 
 * Uses the same width detection logic as CalendarWithTabs:
 * - Checks isMobile from react-device-detect
 * - Uses ResizeObserver to track container width
 * - Initializes with a large value to assume desktop initially
 */
function DesktopSwitch({ desktop: DesktopComponent, mobile: MobileComponent, desktopWidth = 800, desktopProps = {}, mobileProps = {} }) {
  // State for container width - initialize to a large value to assume desktop initially
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);

  // Determine if desktop view (not mobile and width >= desktopWidth)
  const isDesktop = !isMobile && containerWidth >= desktopWidth;

  // Measure container width SYNCHRONOUSLY before the first paint
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }
  }, []);

  // Track container width with ResizeObserver for subsequent resizes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="h-full">
      {isDesktop ? <DesktopComponent {...desktopProps} /> : <MobileComponent {...mobileProps} />}
    </div>
  );
}

export default DesktopSwitch;
